import * as React from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { atom, useAtomValue } from 'jotai';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { Language } from '@restworld/utility-types';
import { FormLabelWrapper, HelperTextRElement, TextInput } from '@restworld/ui-ds-base';

import {
  EditFieldOptionsModal,
  EditModalFooter,
  EditModalTitle,
  FieldOptionsViewType,
  IEditModalProps,
} from '../profile-edit-modal';
import { useApiSubmitHandler } from '../../hooks';
import { getWorkerAtom, getWorkerFieldOptionsAtom } from '../../../global-state.worker-data';

const defaultValuesAtom = atom((get) => {
  const { data: workerData } = get(getWorkerAtom);
  return {
    languages: workerData.languages.map((language) => ({
      language: language.language,
      language_level_cefr: language.language_level_cefr,
    })),
  };
});

type ILanguageFormContentProps = IEditModalProps;
type FieldType = {
  language: string;
  language_level_cefr: string;
};
interface InputProps {
  languages: FieldType[];
}
export const LanguageFormContent: React.FunctionComponent<ILanguageFormContentProps> = ({
  onClose,
  editPageTitle,
  mode,
}) => {
  const { t } = useTranslation();
  const defaultValues = useAtomValue(defaultValuesAtom);
  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm<InputProps>({
    reValidateMode: 'onSubmit',
    defaultValues: mode === 'edit' ? defaultValues : {},
  });
  const { data: workerData } = useAtomValue(getWorkerAtom);
  const { apiSubmitHandler, loading } = useApiSubmitHandler({ onClose, editPageType: 'languages' });
  const { data: workerFieldOptionsData } = useAtomValue(getWorkerFieldOptionsAtom);

  const { fieldOptionsView, toggleFieldOptionsView } = useFieldOptions();
  const { fields, append, remove } = useFieldArray<InputProps>({ control, name: 'languages' });

  const onSubmit: SubmitHandler<InputProps> = React.useCallback(
    (params) => {
      try {
        const newLanguage = params.languages.map((language) => ({ ...language }));
        if (mode === 'add')
          apiSubmitHandler({
            languages: [...(workerData?.languages ? workerData.languages : []), ...newLanguage] as Language[],
          });
        else apiSubmitHandler({ languages: newLanguage as Language[] });
      } catch (error) {
        console.error(error, `Error in language form submit`);
      }
    },
    [apiSubmitHandler, mode, workerData.languages]
  );
  React.useEffect(() => {
    if (mode === 'add') append({ language: '', language_level_cefr: '' });
  }, [append, mode]);
  return (
    <div className="w-full">
      <form method="post" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {fields.map((field, fieldIndex) => {
          const fieldLanguages = defaultValues.languages.map((v) => v.language);
          const selectedLanguage = getValues(`languages.${fieldIndex}.language`);
          const selectedLanguages = fieldLanguages?.filter((v) => v !== selectedLanguage);
          const languageOptions = [...workerFieldOptionsData.languages].filter(
            (v) => !selectedLanguages.includes(v.id)
          );
          const checkIfErrored = (fieldKey: keyof FieldType) =>
            errors?.languages?.[fieldIndex] && Object.keys(errors.languages[fieldIndex]).includes(fieldKey);
          return (
            <React.Fragment key={field.id}>
              <EditModalTitle
                onClose={fieldIndex === 0 ? onClose : null}
                editPageTitle={`${editPageTitle ?? ''} ${
                  mode === 'add' ? workerData.languages?.length + 1 : fieldIndex + 1
                }`}
                onDelete={
                  mode === 'edit'
                    ? async () => {
                        await apiSubmitHandler({
                          languages: workerData?.languages.filter((_, index) => index !== fieldIndex),
                        });
                        remove(fieldIndex);
                      }
                    : null
                }
                loading={loading}
              />
              <div className="flex flex-col gap-4 md:flex-row">
                <FormLabelWrapper id={`languages.${fieldIndex}.language`} isFullWidth label={t('common:language')}>
                  <Controller
                    name={`languages.${fieldIndex}.language`}
                    control={control}
                    rules={{ required: mode === 'add' }}
                    render={({ field }) => (
                      <TextInput
                        id={`languages.${fieldIndex}.language`}
                        placeholder={t('onboarding-page:choose_language')}
                        type="text"
                        rightIcon={ChevronDownIcon}
                        helperText={
                          checkIfErrored('language') ? (
                            <HelperTextRElement helperText={t('onboarding-page:this_field_is_required')} />
                          ) : null
                        }
                        color={checkIfErrored('language') ? 'failure' : undefined}
                        readOnly
                        {...field}
                        value={workerFieldOptionsData?.languages?.find((v) => v.id === field.value)?.label || ''}
                        onClick={() => {
                          toggleFieldOptionsView({
                            showOptionsView: true,
                            id: `language`,
                            selected: field.value ?? '',
                            options: languageOptions,
                            mainTitle: t('common:language'),
                            fieldIndex,
                          });
                        }}
                      />
                    )}
                  />
                </FormLabelWrapper>
                <FormLabelWrapper
                  id={`languages.${fieldIndex}.language_level_cefr`}
                  isFullWidth
                  label={t('onboarding-page:knowledge_level')}
                >
                  <Controller
                    name={`languages.${fieldIndex}.language_level_cefr`}
                    control={control}
                    rules={{ required: mode === 'add' }}
                    render={({ field }) => (
                      <TextInput
                        id={`languages.${fieldIndex}.language_level_cefr`}
                        placeholder={t('onboarding-page:choose_level')}
                        type="text"
                        rightIcon={ChevronDownIcon}
                        helperText={
                          checkIfErrored('language_level_cefr') ? (
                            <HelperTextRElement helperText={t('onboarding-page:this_field_is_required')} />
                          ) : null
                        }
                        color={checkIfErrored('language_level_cefr') ? 'failure' : undefined}
                        readOnly
                        {...field}
                        value={
                          workerFieldOptionsData?.languageLevelCertificate?.find((v) => v.id === field.value)?.label ||
                          ''
                        }
                        onClick={() => {
                          toggleFieldOptionsView({
                            showOptionsView: true,
                            id: `language_level_cefr`,
                            selected: field.value ?? '',
                            options: workerFieldOptionsData?.languageLevelCertificate,
                            mainTitle: t('onboarding-page:knowledge_level'),
                            fieldIndex,
                          });
                        }}
                      />
                    )}
                  />
                </FormLabelWrapper>
              </div>
              {fieldIndex !== fields.length - 1 && <hr className="text-primary-300" />}
              <EditFieldOptionsModal
                {...fieldOptionsView}
                mainTitle={fieldOptionsView.mainTitle}
                value={fieldOptionsView.selected ?? getValues(`languages.${fieldIndex}.${fieldOptionsView.id}`)}
                onChange={(value) => {
                  if (typeof value === 'string') {
                    toggleFieldOptionsView((p) => ({ ...p, selected: value }));
                  }
                }}
                onBack={() => toggleFieldOptionsView(defaultModalFieldOptions)}
                onSave={() => {
                  setValue(
                    `languages.${fieldOptionsView.fieldIndex}.${fieldOptionsView.id}`,
                    fieldOptionsView.selected
                  );
                  toggleFieldOptionsView(defaultModalFieldOptions);
                }}
                visible={fieldOptionsView.showOptionsView}
                returnType="string"
              />
            </React.Fragment>
          );
        })}
        <EditModalFooter
          loading={loading}
          onClose={() => {
            onClose();
            reset(defaultValues);
          }}
          editPageType="languages"
        />
      </form>
    </div>
  );
};

export default LanguageFormContent;
interface FieldOptionsModalViewType extends Omit<FieldOptionsViewType, 'id'> {
  id: 'language' | 'language_level_cefr';
  selected: string;
  fieldIndex?: number;
}
const defaultFieldOptions: FieldOptionsModalViewType = {
  showOptionsView: false,
  options: [],
  mainTitle: '',
  subTitle: '',
  id: 'language',
  returnType: 'string',
  selected: '',
};
const defaultModalFieldOptions = {
  ...defaultFieldOptions,
  id: 'language',
} as FieldOptionsModalViewType;
const useFieldOptions = () => {
  const [fieldOptionsView, toggleFieldOptionsView] = React.useState<FieldOptionsModalViewType | null>({
    ...defaultModalFieldOptions,
  });
  return {
    fieldOptionsView,
    toggleFieldOptionsView,
  };
};
